import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Popconfirm, 
  message, 
  Input, 
  Select, 
  Typography,
  Result,
  Radio,
  Popover
} from 'antd';
import { 
  DeleteOutlined,
  PlusOutlined,
  EyeOutlined,
  LoginOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { ResetIcon, RefreshIcon } from '../../components/common/ActionIcons';
import { FiEdit, FiTrash2, FiSave, FiX, FiEye } from 'react-icons/fi';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { 
  IoStatsChart, 
  IoBriefcase, 
  IoPeople, 
  IoCloudUpload, 
  IoCalendar,
  IoPerson,
  IoPricetag,
  IoLocationSharp
} from 'react-icons/io5';
import { BsFillFileSpreadsheetFill } from "react-icons/bs";
import { AiFillExclamationCircle } from 'react-icons/ai';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import axios from 'axios';
import PageWrapper from '../../components/common/PageWrapper';
import { FilePathCard } from '../../components/crud/utils/renderUIComponents';
import { formatUploadDisplayName } from '../../utils/functions';
import { getUploadBatchFileIcon } from './uploadBatchFileIcon';
import { showApiError, normalizeApiErrorText } from '../../utils/formatApiError';
import ApiErrorAlert from '../../components/common/ApiErrorAlert';

import { 
  listUploadBatches, 
  deleteUploadBatch, 
  updateUploadBatch, 
  loadUploadBatchDto 
} from '../../actions/uploadBatchActions';
import { 
  UPLOAD_BATCH_UPDATE_RESET, 
  UPLOAD_BATCH_DELETE_RESET 
} from '../../constants/uploadBatchConstants';
import SubjectListByLocation from '../../components/patient/subject/SubjectListByLocation.component';
import PatientListDrawer from '../../components/patient/PatientListDrawer.component';
import ProcessingJobs from '../../components/patient/subject/processingJob/ProcessingJobs.component';
import LocationExplorer from '../../components/uploadBatch/LocationExplorer.component';
import {
  applySearchFilter,
  applyStatusFilter,
  DEFAULT_UPLOAD_BATCH_LIST_FILTER,
  resolveStatusFilterFromSearch,
} from './uploadBatchListFilters';

const { Text } = Typography;

const EMPTY_UPLOAD_BATCHES = [];

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const hasValidationErrors = (validationErrors) => {
  if (validationErrors == null) return false;
  const text = typeof validationErrors === 'string' ? validationErrors.trim() : String(validationErrors).trim();
  return text.length > 0 && text !== 'null';
};

const parseValidationErrors = (validationErrors) => {
  const text = typeof validationErrors === 'string' ? validationErrors.trim() : String(validationErrors).trim();
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return { kind: 'list', items: parsed.map((item) => (typeof item === 'string' ? item : JSON.stringify(item, null, 2))) };
    }
    if (parsed && typeof parsed === 'object') {
      return { kind: 'json', value: JSON.stringify(parsed, null, 2) };
    }
  } catch {
    // plain text
  }
  return { kind: 'text', value: text };
};

const ValidationErrorsPopoverContent = ({ validationErrors }) => {
  const parsed = parseValidationErrors(validationErrors);
  if (!parsed) return null;

  if (parsed.kind === 'list') {
    return (
      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
        {parsed.items.map((item, index) => (
          <li key={index} style={{ marginBottom: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {item}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <pre style={{
      margin: 0,
      fontSize: 12,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontFamily: 'inherit',
    }}>
      {parsed.value}
    </pre>
  );
};

const UploadBatchListScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tableRef = useRef(null);

  // Authentication check
  const { userInfo } = useSelector((state) => state.userLogin);
  const isAuthenticated = Boolean(userInfo?.token);

  // Redux state
  const uploadBatchList = useSelector(state => state.uploadBatchList);
  const {
    loading: loadingList,
    uploadBatches,
    totalPages,
    currentPage,
    totalCount,
    error: errorList,
  } = uploadBatchList;
  // Stable empty list — default `= []` in destructure creates a new [] every render and
  // loops setLocalUploadBatches when LIST_FAIL omits uploadBatches.
  const batches = uploadBatches ?? EMPTY_UPLOAD_BATCHES;
  const listErrorText = normalizeApiErrorText(errorList);

  const uploadBatchDelete = useSelector(state => state.uploadBatchDelete);
  const { loading: loadingDelete, success: successDelete, error: errorDelete } = uploadBatchDelete;

  const uploadBatchUpdate = useSelector(state => state.uploadBatchUpdate);
  const { loading: loadingUpdate, success: successUpdate, error: errorUpdate, uploadBatch: updatedUploadBatch } = uploadBatchUpdate;

  // Local state
  const [filter, setFilter] = useState(DEFAULT_UPLOAD_BATCH_LIST_FILTER);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchProp, setSearchProp] = useState('fileName');
  const [statusFilter, setStatusFilter] = useState('all');
  const [localUploadBatches, setLocalUploadBatches] = useState([]);
  
  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedUploadBatchId, setSelectedUploadBatchId] = useState(null);

  // Subject drawer state
  const [subjectDrawerVisible, setSubjectDrawerVisible] = useState(false);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);

  // Patient drawer state — for batches owned directly by a Patient (e.g. CARTO/EnSiteX
  // conversions), which have no subjectIds.
  const [patientDrawerVisible, setPatientDrawerVisible] = useState(false);
  const [selectedPatientIds, setSelectedPatientIds] = useState([]);

  // Location explorer state
  const [locationExplorerVisible, setLocationExplorerVisible] = useState(false);
  const [selectedUploadBatchIdForLocation, setSelectedUploadBatchIdForLocation] = useState(null);

  // Initialize and sync data
  useEffect(() => {
    dispatch({ type: UPLOAD_BATCH_UPDATE_RESET });
    dispatch({ type: UPLOAD_BATCH_DELETE_RESET });
  }, [dispatch]);

  useEffect(() => {
    setLocalUploadBatches(batches);
  }, [batches]);

  useEffect(() => {
    if (listErrorText) showApiError(listErrorText, t);
  }, [listErrorText, t]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(loadUploadBatchDto());
      dispatch(listUploadBatches(filter));
    }
  }, [dispatch, isAuthenticated, filter]);

  // Handle success/error states
  useEffect(() => {
    if (successDelete) {
      message.success(t('Upload batch deleted successfully'));
      dispatch(listUploadBatches(filter));
      setTimeout(() => dispatch({ type: UPLOAD_BATCH_DELETE_RESET }), 100);
    }
  }, [successDelete, dispatch, filter, t]);

  useEffect(() => {
    if (successUpdate && editingRowId && updatedUploadBatch) {
      setLocalUploadBatches(prevBatches => 
        prevBatches.map(batch => 
          batch.id === updatedUploadBatch.id ? { ...batch, ...updatedUploadBatch } : batch
        )
      );
      message.success(t('Upload batch updated successfully'));
      setEditingRowId(null);
      setEditingData({});
      setTimeout(() => dispatch({ type: UPLOAD_BATCH_UPDATE_RESET }), 100);
    }
  }, [successUpdate, editingRowId, t, updatedUploadBatch, dispatch]);

  useEffect(() => {
    if (errorUpdate && editingRowId) {
      message.error(Array.isArray(errorUpdate) ? errorUpdate.join(', ') : errorUpdate);
      setTimeout(() => dispatch({ type: UPLOAD_BATCH_UPDATE_RESET }), 100);
    }
    if (errorDelete) {
      message.error(t('Failed to delete upload batch'));
      setTimeout(() => dispatch({ type: UPLOAD_BATCH_DELETE_RESET }), 100);
    }
  }, [errorUpdate, errorDelete, editingRowId, t, dispatch]);

  // Handlers
  const handleRefresh = useCallback(() => {
    dispatch(listUploadBatches(filter));
  }, [dispatch, filter]);

  const handleDelete = useCallback((id) => {
    dispatch(deleteUploadBatch(id));
  }, [dispatch]);

  const handleBulkDelete = useCallback(async () => {
    for (const key of selectedRowKeys) {
      dispatch(deleteUploadBatch(key));
    }
    setSelectedRowKeys([]);
    message.success(t('Selected upload batches deleted'));
  }, [selectedRowKeys, dispatch, t]);

  const handleEdit = useCallback((record) => {
    setEditingRowId(record.id);
    setEditingData({ ...record });
  }, []);

  const handleSave = useCallback(() => {
    if (editingRowId && editingData) {
      const currentBatch = localUploadBatches.find(b => b.id === editingRowId);
      const changes = {};
      Object.keys(editingData).forEach(key => {
        if (editingData[key] !== currentBatch[key]) {
          changes[key] = editingData[key];
        }
      });

      if (Object.keys(changes).length > 0) {
        // Always include organisationId as it's required by the PATCH endpoint
        if (!changes.organisationId && currentBatch.organisationId) {
          changes.organisationId = currentBatch.organisationId;
        }
        dispatch(updateUploadBatch(editingRowId, changes));
      } else {
        setEditingRowId(null);
        setEditingData({});
      }
    }
  }, [editingRowId, editingData, localUploadBatches, dispatch]);

  const handleCancel = useCallback(() => {
    setEditingRowId(null);
    setEditingData({});
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setEditingData(prev => ({ ...prev, [field]: value }));
  }, []);

  const statusOptions = [
    { label: t('All'), value: 'all' },
    { label: t('Pending'), value: 'Pending' },
    { label: t('Validated'), value: 'Validated' },
    { label: t('Processing'), value: 'Processing' },
    { label: t('Rejected'), value: 'Rejected' },
    { label: t('Completed'), value: 'Completed' },
    { label: t('Failed'), value: 'Failed' }
  ];

  const handleViewDetails = useCallback((record) => {
    // Check if the record has subject IDs to show subjects, a direct patientId (CARTO/EnSiteX
    // batches) to show that patient, otherwise fall back to batch details.
    if (record.subjectIds && record.subjectIds.length > 0) {
      setSelectedSubjectIds(record.subjectIds);
      setSubjectDrawerVisible(true);
    } else if (record.patientId) {
      setSelectedPatientIds([record.patientId]);
      setPatientDrawerVisible(true);
    } else {
      // Fall back to the original upload batch detail drawer
      setSelectedUploadBatchId(record.id);
      setDrawerVisible(true);
    }
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerVisible(false);
    setSelectedUploadBatchId(null);
  }, []);

  const handleCloseSubjectDrawer = useCallback(() => {
    setSubjectDrawerVisible(false);
    setSelectedSubjectIds([]);
  }, []);

  const handleClosePatientDrawer = useCallback(() => {
    setPatientDrawerVisible(false);
    setSelectedPatientIds([]);
  }, []);

  const handleOpenLocationExplorer = useCallback((uploadBatchId) => {
    setSelectedUploadBatchIdForLocation(uploadBatchId);
    setLocationExplorerVisible(true);
  }, []);

  const handleCloseLocationExplorer = useCallback(() => {
    setLocationExplorerVisible(false);
    setSelectedUploadBatchIdForLocation(null);
  }, []);

  /** Merge one batch from GET /uploadbatch/:id into local table state — no list reload (avoids expanded-row flash). */
  const refreshUploadBatchRow = useCallback(
    async (batchId) => {
      if (!batchId || !userInfo?.token) return;
      const authHeaders = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo.token}`,
      };
      try {
        // GET by id recomputes stale pipelineReports.parity (five-entity → nine-entity) before returning.
        const { data } = await axios.get(`/dotnet_api/v1/uploadbatch/${batchId}`, {
          headers: authHeaders,
        });
        setLocalUploadBatches((prev) =>
          prev.map((b) => (b.id === batchId ? { ...b, ...data } : b))
        );
      } catch (err) {
        const detail =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          t('Could not refresh upload batch');
        message.error(typeof detail === 'string' ? detail : t('Could not refresh upload batch'));
      }
    },
    [userInfo?.token, t]
  );

  // Open processing jobs drawer for this upload batch
  const handleFileClick = useCallback((record) => {
    handleViewDetails(record);
  }, [handleViewDetails]);

  const handleSearch = (value) => {
    setSearchText(value);
    if (searchProp === 'status') {
      setStatusFilter(resolveStatusFilterFromSearch(value));
    } else {
      setStatusFilter('all');
    }
    setFilter((prev) => applySearchFilter(prev, searchProp, value));
  };

  const handleSearchPropChange = (value) => {
    setSearchProp(value);
    if (value !== 'status') {
      setStatusFilter('all');
    }
    setFilter((prev) => applySearchFilter(prev, value, searchText));
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    if (value === 'all') {
      setFilter((prev) => applyStatusFilter(prev, value));
      if (filter.filter === 'status') {
        setSearchText('');
        setSearchProp('fileName');
      }
      return;
    }
    setSearchProp('status');
    setSearchText(value);
    setFilter((prev) => applyStatusFilter(prev, value));
  };

  const handleReset = () => {
    setSearchText('');
    setSearchProp('fileName');
    setStatusFilter('all');
    setFilter(DEFAULT_UPLOAD_BATCH_LIST_FILTER);
  };

  // Search and filter options
  const searchOptions = [
    { value: 'fileName', label: t('File Name') },
    { value: 'fileFormat', label: t('File Format') },
    { value: 'status', label: t('Status') },
    { value: 'uploadedBy', label: t('Uploaded By') },
    { value: 'customerPrefix', label: t('Customer Prefix') }
  ];

  // Expandable row configuration
  const expandedRowRender = (record) => {
    return (
      <div style={{ padding: '12px 16px' }}>
        {/* Processing Jobs Section */}
        <ProcessingJobs
          uploadBatchId={record.id}
          uploadBatchStatus={record.status}
          title="Processing Jobs"
          showPagination={false}
          defaultPageSize={5}
          height={400}
          uploadBatchFileStats={{
            totalRecords: record.totalRecords,
            processedRecords: record.processedRecords,
            failedRecords: record.failedRecords,
            validRecords: record.validRecords,
          }}
          filePath={record.filePath}
          fileHash={record.fileHash}
          uploadMethod={record.uploadMethod}
          customerPrefix={record.customerPrefix}
          createdAt={record.createdAt}
          onRefreshUploadBatch={refreshUploadBatchRow}
        />
      </div>
    );
  };

  const expandable = {
    expandedRowRender,
    rowExpandable: (record) => true,
  };

  // Table columns
  const columns = [
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'left',
      width: 70,
      render: (_, record) => {
        const isEditing = editingRowId === record.id;
        return (
          <Space size="small">
            {isEditing ? (
              <>
                <Button type="primary" size="small" icon={<FiSave />} onClick={handleSave} loading={loadingUpdate} />
                <Button size="small" icon={<FiX />} onClick={handleCancel} />
              </>
            ) : (
              <>
                <Button type="text" size="small" icon={<FiEdit />} onClick={() => handleEdit(record)} />
                <Popconfirm
                  title={t('Delete Upload Batch')}
                  onConfirm={() => handleDelete(record.id)}
                  okText={t('Yes')}
                  cancelText={t('No')}
                >
                  <Button type="text" size="small" icon={<FiTrash2 />} danger loading={loadingDelete} />
                </Popconfirm>
              </>
            )}
          </Space>
        );
      }
    },
    {
      title: t('File'),
      dataIndex: 'fileName',
      key: 'fileName',
      sorter: true,
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Input
            value={editingData.fileName || text}
            onChange={(e) => handleInputChange('fileName', e.target.value)}
            size="small"
          />
        ) : (
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--ant-color-primary-bg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onClick={() => handleFileClick(record)}
            title={t('Click to view processing details')}
          >
            {getUploadBatchFileIcon(record.fileFormat)}
            <div style={{ minWidth: 0, maxWidth: 280, overflow: 'hidden' }}>
              <div style={{ fontWeight: 'bold', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={text}>
                {formatUploadDisplayName(text)}
              </div>
              <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                {record.fileFormat?.toUpperCase()} • {formatFileSize(record.fileSize)}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (text, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Select
            value={editingData.status || text}
            onChange={(value) => handleInputChange('status', value)}
            size="small"
            style={{ width: '100%' }}
          >
            <Select.Option value="Pending">Pending</Select.Option>
            <Select.Option value="Validated">Validated</Select.Option>
            <Select.Option value="Processing">Processing</Select.Option>
            <Select.Option value="Rejected">Rejected</Select.Option>
            <Select.Option value="Completed">Completed</Select.Option>
            <Select.Option value="Failed">Failed</Select.Option>
          </Select>
        ) : (
          <Space size={4} align="center">
            <Tag
              size="small"
              color={
                text === 'Rejected' ? 'red' :
                text === 'Completed' ? 'green' :
                text === 'Processing' ? 'blue' :
                text === 'Pending' ? 'orange' :
                text === 'Validated' ? 'green' :
                'default'
              }
            >
              {text}
            </Tag>
            {hasValidationErrors(record.validationErrors) && (
              <Popover
                content={
                  <div style={{ width: 600, maxHeight: 500, overflowY: 'auto' }}>
                    <ValidationErrorsPopoverContent validationErrors={record.validationErrors} />
                  </div>
                }
                title={t('Validation Errors')}
                trigger="click"
                placement="bottomLeft"
              >
                <Button
                  type="text"
                  size="small"
                  icon={<AiFillExclamationCircle style={{ color: '#ff4d4f' }} />}
                  style={{ padding: 0, height: 'auto' }}
                  onClick={(e) => e.stopPropagation()}
                  title={t('Validation Errors')}
                />
              </Popover>
            )}
          </Space>
        );
      }
    },
    {
      title: t('Validity'),
      dataIndex: 'isFormatValid',
      key: 'isFormatValid',
      render: (isValid, record) => {
        const isEditing = editingRowId === record.id;
        return isEditing ? (
          <Select
            value={editingData.isFormatValid !== undefined ? editingData.isFormatValid : isValid}
            onChange={(value) => handleInputChange('isFormatValid', value)}
            size="small"
            style={{ width: '100%' }}
          >
            <Select.Option value={true}>Valid</Select.Option>
            <Select.Option value={false}>Invalid</Select.Option>
          </Select>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isValid ? (
              <FaCheckCircle style={{ color: '#52c41a', fontSize: '16px' }} />
            ) : (
              <FaTimesCircle style={{ color: '#f5222d', fontSize: '16px' }} />
            )}
          </div>
        );
      }
    },
    {
      title: t('MAT File'),
      dataIndex: 'matFilePath',
      key: 'matFilePath',
      render: (matFilePath) => (
        <FilePathCard filePath={matFilePath} emptyText={t('Not yet generated')} userInfo={userInfo} />
      )
    },
    {
      title: t('Uploaded By'),
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      render: (text) => <Text style={{ fontSize: '12px' }}>{text}</Text>
    },
    {
      title: t('Upload Date'),
      dataIndex: 'uploadDateTime',
      key: 'uploadDateTime',
      sorter: true,
      render: (date) => (
        <Text style={{ fontSize: '11px' }}>
          {date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-'}
        </Text>
      )
    },
    {
      title: t('Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (date) => (
        <Text style={{ fontSize: '11px' }}>
          {dayjs(date).format('YYYY-MM-DD HH:mm:ss')}
        </Text>
      )
    }
  ];

  // ResizeObserver cleanup
  useEffect(() => {
    let resizeObserver;
    if (tableRef.current) {
      resizeObserver = new ResizeObserver(
        debounce(() => {
          // Optional: Update any table width state if needed
        }, 100)
      );
      resizeObserver.observe(tableRef.current);
    }
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const headerActions = (
    <Space>
      {selectedRowKeys.length > 0 && (
        <Popconfirm
          title={t("Delete selected items")}
          description={t("Are you sure?")}
          onConfirm={handleBulkDelete}
        >
          <Button type="primary" danger size="small">
            {t("Delete")} ({selectedRowKeys.length})
          </Button>
        </Popconfirm>
      )}
    </Space>
  );

  // Authentication guard
  if (!isAuthenticated) {
    return (
      <PageWrapper
        title={t('Upload Batches')}
        icon={<BsFillFileSpreadsheetFill size={24} />}
        showCard={true}
      >
        <Result
          icon={<LoginOutlined />}
          title={t('Authentication Required')}
          subTitle={t('Please log in to view upload batches.')}
          extra={
            <Button type="primary" onClick={() => window.location.href = '/userlogin'}>
              {t('Go to Login')}
            </Button>
          }
        />
      </PageWrapper>
    );
  }

  const displayTotalCount = totalCount ?? 0;

  return (
    <PageWrapper
      title={t('Upload Batches')}
      subtitle={
        <Text type="secondary" style={{ fontSize: 14 }}>
          ({displayTotalCount} {t('items')})
        </Text>
      }
      icon={<BsFillFileSpreadsheetFill size={24} />}
      headerActions={headerActions}
      showCard={true}
      cardProps={{
        size: 'small'
      }}
    >
      {listErrorText && <ApiErrorAlert error={errorList} />}

      {/* Search and Filter Controls */}
      <div style={{ marginBottom: 16 }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <span style={{ fontSize: '12px', color: '#666' }}>{t('Search')}:</span>
          <Select
            placeholder={t("Search by")}
            style={{ width: 140 }}
            size="small"
            value={searchProp}
            onChange={handleSearchPropChange}
          >
            {searchOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
          <Input.Search
            placeholder={`${t("Search by")} ${searchOptions.find(opt => opt.value === searchProp)?.label || ''}`}
            onSearch={handleSearch}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
            size="small"
          />
          <Button icon={<ResetIcon />} onClick={handleReset} size="small">
            {t("Reset")}
          </Button>
          <Button icon={<RefreshIcon />} onClick={handleRefresh} size="small">
            {t('Refresh')}
          </Button>
        </Space>

        <div>
          <Text style={{ fontSize: '12px', color: '#666', marginRight: 8 }}>{t('Status')}:</Text>
          <Radio.Group
            value={statusFilter}
            onChange={handleStatusFilterChange}
            size="small"
            buttonStyle="solid"
          >
            {statusOptions.map((option) => (
              <Radio.Button key={option.value} value={option.value}>
                {option.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
      </div>

      <div ref={tableRef} style={{ width: '100%', overflow: 'auto' }}>
        <Table
          columns={columns}
          dataSource={localUploadBatches}
          rowKey="id"
          loading={loadingList}
          size="small"
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          expandable={expandable}
          pagination={{
            current: currentPage,
            pageSize: filter.limit,
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`,
            pageSizeOptions: ['10', '20', '50', '100'],
            size: 'small',
            onChange: (page, pageSize) => {
              setFilter((prev) => ({ ...prev, page, limit: pageSize }));
            },
          }}
          onChange={(pagination, _filters, sorter) => {
            setFilter((prev) => ({
              ...prev,
              sortBy: sorter.field || 'createdAt',
              sortOrder: sorter.order === 'ascend' ? 'asc' : 'desc',
            }));
          }}
          scroll={{ x: 1100 }}
          tableLayout="auto"
        />
      </div>

      {/* Subject List Drawer */}
      <SubjectListByLocation
        visible={subjectDrawerVisible}
        onClose={handleCloseSubjectDrawer}
        subjectIds={selectedSubjectIds}
      />

      {/* Patient Details Drawer — for batches owned directly by a Patient (CARTO/EnSiteX) */}
      <PatientListDrawer
        visible={patientDrawerVisible}
        onClose={handleClosePatientDrawer}
        patientIds={selectedPatientIds}
        title={t('Patient Details')}
      />

      {/* Location Explorer Drawer */}
      <LocationExplorer
        visible={locationExplorerVisible}
        onClose={handleCloseLocationExplorer}
        uploadBatchId={selectedUploadBatchIdForLocation}
      />
    </PageWrapper>
  );
};

export default UploadBatchListScreen;
